import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import type { IStyle } from 'fela';
import { graphql } from 'gatsby';

import { useFelaEnhanced, type RulesExtend } from '@ackee/fela';

import { Language, languageSpecificConfig } from 'config/language';

import { LocalizedLink } from '../../../ui';
import type { PostCategory } from '../../types';

import * as felaRules from './CategoriesList.rules';

export interface CategoriesListProps {
    categories: PostCategory[];
    showAllCategory?: boolean;
    activeStyle?: IStyle;
    extend?: RulesExtend<typeof felaRules>;
    languageMutation?: Language;
}

interface CategoryWithLink extends Omit<PostCategory, 'slug'> {
    to: string;
}

export const CategoriesList = ({
    categories,
    showAllCategory = false,
    activeStyle,
    extend,
    languageMutation,
}: CategoriesListProps) => {
    const { formatMessage } = useIntl();
    const { styles } = useFelaEnhanced(felaRules, { extend });

    const categoriesWithLinks: CategoryWithLink[] = useMemo(() => {
        if (!!categories) {
            const languagePrefix = languageMutation ? languageSpecificConfig[languageMutation].SITE_URL : '';
            return categories.map(c => ({ ...c, to: languagePrefix + `/blog/category/${c.slug}` }));
        } else {
            return [{ to: '', title: '' }];
        }
    }, [categories, languageMutation]);

    categoriesWithLinks.sort((a, b) => a.title.localeCompare(b.title));

    const categoriesToRender = showAllCategory
        ? [
              {
                  to: '/blog',
                  title: formatMessage({ id: 'blog.category.all' }),
              },
          ].concat(categoriesWithLinks)
        : categoriesWithLinks;

    return (
        <ul className={styles.container}>
            {categoriesToRender.map(category => (
                <li key={category.to} className={styles.listItem}>
                    {languageMutation ? (
                        <a href={category.to} className={styles.link}>
                            {category.title}
                        </a>
                    ) : (
                        <LocalizedLink to={category.to} className={styles.link} activeStyle={activeStyle}>
                            {category.title}
                        </LocalizedLink>
                    )}
                </li>
            ))}
        </ul>
    );
};

export const query = graphql`
    fragment PostCategory on STRAPI_CATEGORY {
        slug
        title
    }
`;
